"use client";

import { Languages } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import {
  localizedMarketingPath,
  MARKETING_LOCALES,
  marketingLocaleFromPath,
  MARKETING_LOCALE_COOKIE,
  MARKETING_LANGUAGE_LABELS,
  MARKETING_LOCALE_LABELS,
  type MarketingLocale,
} from "@/lib/i18n/marketingLocale";

function rememberMarketingLocale(locale: MarketingLocale) {
  document.cookie = `${MARKETING_LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

export function MarketingLanguageSwitcher({ compact = false, className = "" }: {
  compact?: boolean;
  className?: string;
}) {
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const router = useRouter();
  const currentLocale = marketingLocaleFromPath(pathname) ?? "en";
  const languageLabel = MARKETING_LANGUAGE_LABELS[currentLocale];

  // The root layout persists across a client-side locale navigation. Keep the
  // document direction in sync immediately instead of waiting for a full
  // document reload to remount the layout.
  useEffect(() => {
    const locale = marketingLocaleFromPath(pathname) ?? "en";
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [pathname]);

  return (
    <label
      className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors ${className}`}
      data-i18n-skip
    >
      <Languages className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="sr-only">{languageLabel}</span>
      <select
        value={currentLocale}
        aria-label={languageLabel}
        onChange={(event) => {
          const targetLocale = event.target.value as MarketingLocale;
          rememberMarketingLocale(targetLocale);
          const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
          router.push(localizedMarketingPath(`${pathname}${suffix}`, targetLocale));
        }}
        className={`cursor-pointer bg-transparent text-xs text-muted-foreground outline-none hover:text-foreground ${compact ? "max-w-[7.5rem]" : ""}`}
      >
        {MARKETING_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {MARKETING_LOCALE_LABELS[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
